import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Mail, Phone, MapPin, Clock, Send, ArrowUp, 
  Facebook, Instagram, Linkedin, Youtube, 
  Truck, Shield, CreditCard, 
  Headphones, Lock, ChevronRight
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const Footer: React.FC = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    setTimeout(() => {
      setSubmitMessage("Merci pour votre inscription ! 🎉");
      setEmail("");
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(""), 3000);
    }, 1000);
  };

  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: "Accueil", path: "/" },
    { name: "Nos produits", path: "/products" },
    { name: "Catégories", path: "/categories" },
    { name: "Promotions", path: "/promotions" },
    { name: "Nouveautés", path: "/new-arrivals" },
  ];

  const socialLinks = [
    { icon: Facebook, href: "https://facebook.com", label: "Facebook", color: "hover:bg-[#1877f2]" },
    { icon: Instagram, href: "https://instagram.com", label: "Instagram", color: "hover:bg-gradient-to-r from-[#f09433] to-[#bc1888]" },
    { icon: Linkedin, href: "https://linkedin.com", label: "LinkedIn", color: "hover:bg-[#0a66c2]" },
    { icon: Youtube, href: "https://youtube.com", label: "YouTube", color: "hover:bg-[#ff0000]" },
  ];

  const contactInfo = [
    { icon: MapPin, text: "Cotonou, Bénin" },
    { icon: Phone, text: "+229 01 57 52 56 06", href: "tel:+2290157525606" },
    { icon: Mail, text: "contact@nissigroupintl.com", href: "mailto:contact@nissigroupintl.com" },
    { icon: Clock, text: "Lun - Sam: 8h - 20h" },
  ];

  return (
    <>
      {/* Bouton retour en haut */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            onClick={scrollToTop}
            className="fixed bottom-16 right-6 z-50 w-12 h-12 rounded-full bg-gradient-to-r from-primary to-primaryHover text-white shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
            aria-label="Retour en haut"
          >
            <ArrowUp className="w-5 h-5 group-hover:-translate-y-1 transition-transform duration-300" />
          </motion.button>
        )}
      </AnimatePresence>

      <footer className="relative w-full overflow-hidden">
        {/* Image de fond - visible avec opacity 100% */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url('/assets/images/data/nissi_footer_bg.png')`,
          }}
        />
        
        {/* Overlay avec couleur primary à 25% d'opacité (pour laisser l'image visible à 75%) */}
        <div className="absolute inset-0 bg-primary/25" />
        
        {/* Contenu du footer */}
        <div className="relative z-10">
          

          {/* Footer principal */}
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
              
              {/* Colonne 1 - Logo & Description */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="space-y-4"
              >
                <Link to="/" className="inline-block active:opacity-70 transition-opacity">
                  <img 
                    src="/assets/logo/v2/logonissirec.png" 
                    alt="NISSI GROUP INTL" 
                    className="h-16 lg:h-20 w-auto object-contain bg-white p-3 rounded-xl shadow-lg"
                  />
                </Link>
                <p className="text-white/90 text-sm leading-relaxed">
                  Votre destination incontournable pour l'achat de produits high-tech de qualité. 
                  Plus de 500 clients satisfaits.
                </p>
                <div className="flex gap-3 pt-2">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={index}
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-9 h-9 rounded-full bg-white/20 flex items-center justify-center transition-all duration-300 hover:bg-white/30 ${social.color}`}
                      aria-label={social.label}
                    >
                      <social.icon className="w-4 h-4 text-white transition-colors" />
                    </motion.a>
                  ))}
                </div>
              </motion.div>

              {/* Colonne 2 - Liens rapides */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                viewport={{ once: true }}
                className="space-y-4"
              >
                <h3 className="text-lg font-semibold text-white relative inline-block">
                  Liens rapides
                  <span className="absolute -bottom-2 left-0 w-8 h-0.5 bg-white/50 rounded-full"></span>
                </h3>
                <ul className="space-y-2">
                  {quickLinks.map((link, index) => (
                    <li key={index}>
                      <Link 
                        to={link.path}
                        className="text-white/90 hover:text-white transition-colors duration-300 text-sm flex items-center gap-2 group"
                      >
                        <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-all group-hover:translate-x-0 -translate-x-1" />
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Colonne 3 - Contact */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                viewport={{ once: true }}
                className="space-y-4"
              >
                <h3 className="text-lg font-semibold text-white relative inline-block">
                  Contact
                  <span className="absolute -bottom-2 left-0 w-8 h-0.5 bg-white/50 rounded-full"></span>
                </h3>
                <ul className="space-y-3">
                  {contactInfo.map((item, index) => (
                    <li key={index}>
                      {item.href ? (
                        <a 
                          href={item.href}
                          className="flex items-center gap-3 text-white/90 hover:text-white transition-colors duration-300 text-sm group"
                        >
                          <item.icon className="w-4 h-4 flex-shrink-0 group-hover:scale-110 transition-transform" />
                          <span className="break-all">{item.text}</span>
                        </a>
                      ) : (
                        <div className="flex items-center gap-3 text-white/90 text-sm">
                          <item.icon className="w-4 h-4 flex-shrink-0" />
                          <span>{item.text}</span>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Colonne 4 - Newsletter */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                viewport={{ once: true }}
                className="space-y-4"
              >
                <h3 className="text-lg font-semibold text-white relative inline-block">
                  Newsletter
                  <span className="absolute -bottom-2 left-0 w-8 h-0.5 bg-white/50 rounded-full"></span>
                </h3>
                <p className="text-white/90 text-sm">
                  Recevez nos offres exclusives et nouveautés.
                </p>
                <form onSubmit={handleSubmit} className="space-y-3">
                  <div className="relative">
                    <input 
                      type="email" 
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Votre email"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/80 focus:outline-none focus:border-white focus:ring-1 focus:ring-white transition-all duration-300 text-sm"
                    />
                  </div>
                  <motion.button 
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={isSubmitting}
                    className="w-full py-3 rounded-xl bg-white text-primary font-medium text-sm hover:shadow-lg hover:shadow-white/25 transition-all duration-300 flex items-center justify-center gap-2 group disabled:opacity-70"
                  >
                    {isSubmitting ? (
                      "Envoi en cours..."
                    ) : (
                      <>
                        <span>S'abonner</span>
                        <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </motion.button>
                  {submitMessage && (
                    <p className="text-green-300 text-xs text-center animate-pulse">
                      {submitMessage}
                    </p>
                  )}
                </form>
              </motion.div>
            </div>
          </div>

          {/* Copyright - Bas du footer */}
          <div className="border-t border-white/10">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <p className="text-white/80 text-xs text-center md:text-left">
                  &copy; {currentYear} <span className="text-white font-semibold">NISSI GROUP INTL</span>. Tous droits réservés.
                </p>
                
                <div className="flex flex-wrap justify-center gap-4 md:gap-6">
                  {["mentions-legales", "cgv", "confidentialite"].map((path, i) => (
                    <Link 
                      key={i}
                      to={`/${path}`}
                      className="text-white/80 hover:text-white text-xs transition-colors duration-300"
                    >
                      {path === "mentions-legales" && "Mentions légales"}
                      {path === "cgv" && "CGV"}
                      {path === "confidentialite" && "Confidentialité"}
                    </Link>
                  ))}
                </div>
                
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    <div className="w-7 h-7 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
                      <CreditCard className="w-3 h-3 text-white/80" />
                    </div>
                    <div className="w-7 h-7 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
                      <Shield className="w-3 h-3 text-white/80" />
                    </div>
                    <div className="w-7 h-7 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
                      <Lock className="w-3 h-3 text-white/80" />
                    </div>
                  </div>
                  <span className="text-white/80 text-[10px] font-medium">Paiement sécurisé</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;